import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  getInventory,
  exportInventoryXlsx,
} from "../services/inventoryService.js";
import api from "../config/api.js";

// ─────────────────────────────────────────────
//  CONSTANTES
// ─────────────────────────────────────────────

const TYPES = [
  { value: "COMPUTER",    label: "Poste",             icon: "bi-pc-display-horizontal", color: "#3B82F6" },
  { value: "SERVER",      label: "Serveur",          icon: "bi-hdd-rack",              color: "#8B5CF6" },
  { value: "PRINTER",     label: "Imprimante",        icon: "bi-printer",               color: "#F59E0B" },
  { value: "NETWORK",     label: "Réseau",            icon: "bi-router",                color: "#10B981" },
  { value: "SCREEN",      label: "Écran",             icon: "bi-display",               color: "#06B6D4" },
  { value: "PHONE",       label: "Téléphone",         icon: "bi-phone",                 color: "#EC4899" },
  { value: "OTHER",       label: "Autre",             icon: "bi-box",                   color: "#6B7280" },
];

const STATUSES = [
  { value: "ACTIVE",   label: "En service",   color: "#10B981", bg: "rgba(16,185,129,.1)"  },
  { value: "INACTIVE", label: "Hors service", color: "#F59E0B", bg: "rgba(245,158,11,.1)"  },
  { value: "RETIRED",  label: "Au rebut",     color: "#6B7280", bg: "rgba(107,114,128,.1)" },
];

// Statut Atera (online/offline) — vient de la sync en temps réel
const ATERA_STATUSES = [
  { value: "ONLINE",  label: "En ligne",   color: "#10B981", bg: "rgba(16,185,129,.1)" },
  { value: "OFFLINE", label: "Hors ligne", color: "#DC2626", bg: "rgba(220,38,38,.1)"  },
];

const WARRANTY = [
  { value: "ACTIVE",   label: "Garantie active",  color: "#10B981", bg: "rgba(16,185,129,.1)"  },
  { value: "EXPIRING", label: "Expire bientôt",   color: "#F59E0B", bg: "rgba(245,158,11,.1)"  },
  { value: "EXPIRED",  label: "Expirée",           color: "#DC2626", bg: "rgba(220,38,38,.1)"   },
  { value: "NONE",     label: "Sans garantie",     color: "#9CA3AF", bg: "rgba(156,163,175,.1)" },
];

const PAGE_SIZE = 20;

// ─────────────────────────────────────────────
//  HELPERS
// ─────────────────────────────────────────────

function getTypeInfo(type) {
  return TYPES.find(t => t.value === type) || TYPES[TYPES.length - 1];
}

function getWarrantyInfo(status) {
  return WARRANTY.find(w => w.value === status) || WARRANTY[WARRANTY.length - 1];
}

function getStatusInfo(status) {
  return STATUSES.find(s => s.value === status) || STATUSES[0];
}

function Badge({ label, color, bg }) {
  return (
    <span style={{ display:"inline-flex", alignItems:"center", gap:4, fontSize:11, fontWeight:600, padding:"3px 8px", borderRadius:20, background:bg, color }}>
      {label}
    </span>
  );
}

// ─────────────────────────────────────────────
//  MODAL FORMULAIRE — Créer / Modifier
// ─────────────────────────────────────────────

function InventoryFormModal({ item, companies, onSave, onClose }) {
  const isEdit = !!item?.id;
  const [form, setForm] = useState({
    companyId:    item?.companyId    || "",
    name:         item?.name         || "",
    type:         item?.type         || "WORKSTATION",
    brand:        item?.brand        || "",
    model:        item?.model        || "",
    serialNumber: item?.serialNumber || "",
    location:     item?.location     || "",
    cpu:          item?.cpu          || "",
    ramGb:        item?.ramGb        || "",
    diskGb:       item?.diskGb       || "",
    diskType:     item?.diskType     || "SSD",
    os:           item?.os           || "",
    osVersion:    item?.osVersion    || "",
    ipAddress:    item?.ipAddress    || "",
    macAddress:   item?.macAddress   || "",
    purchaseDate: item?.purchaseDate || "",
    startDate:    item?.startDate    || "",
    warrantyEnd:  item?.warrantyEnd  || "",
    purchasePrice:"",
    supplier:     item?.supplier     || "",
    status:       item?.status       || "ACTIVE",
    notes:        item?.notes        || "",
  });

  // Convertir le prix (centimes → euros) pour l'affichage
  useEffect(() => {
    if (item?.purchasePrice) setForm(f => ({ ...f, purchasePrice: (item.purchasePrice / 100).toFixed(2) }));
  }, [item]);

  const [saving, setSaving] = useState(false);
  const [error,  setError]  = useState("");
  const [tab,    setTab]    = useState("general");

  const set = (key, val) => setForm(f => ({ ...f, [key]: val }));

  const handleSave = async () => {
    if (!form.name.trim())    return setError("Le nom est obligatoire");
    if (!form.companyId)      return setError("L'entreprise est obligatoire");
    setSaving(true);
    setError("");
    try {
      // Convertir le prix en centimes
      const payload = {
        ...form,
        ramGb:         form.ramGb    ? parseInt(form.ramGb)    : null,
        diskGb:        form.diskGb   ? parseInt(form.diskGb)   : null,
        purchasePrice: form.purchasePrice ? Math.round(parseFloat(form.purchasePrice) * 100) : null,
        purchaseDate:  form.purchaseDate  || null,
        startDate:     form.startDate     || null,
        warrantyEnd:   form.warrantyEnd   || null,
      };
      await onSave(form.companyId, payload, item?.id);
      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const TABS = [
    { id:"general",  label:"Général"     },
    { id:"tech",     label:"Technique"   },
    { id:"lifecycle",label:"Cycle de vie"},
  ];

  return (
    <div style={{ position:"fixed", inset:0, zIndex:1060, background:"rgba(0,0,0,.45)", backdropFilter:"blur(4px)", display:"flex", alignItems:"center", justifyContent:"center", padding:16 }}
      onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{ background:"#fff", borderRadius:16, width:"100%", maxWidth:580, maxHeight:"90vh", display:"flex", flexDirection:"column", boxShadow:"0 24px 80px rgba(0,0,0,.2)", overflow:"hidden" }}>

        {/* Header */}
        <div style={{ padding:"18px 24px 14px", borderBottom:"1px solid #E5E7EB", display:"flex", alignItems:"center", justifyContent:"space-between" }}>
          <h3 style={{ fontFamily:"Syne,sans-serif", fontSize:17, fontWeight:800, color:"#1A1A1A", margin:0 }}>
            {isEdit ? "Modifier l'équipement" : "Ajouter un équipement"}
          </h3>
          <button onClick={onClose} style={{ background:"none", border:"none", fontSize:16, color:"#9CA3AF", cursor:"pointer" }}>
            <i className="bi bi-x-lg"/>
          </button>
        </div>

        {/* Onglets */}
        <div style={{ display:"flex", borderBottom:"1px solid #E5E7EB", padding:"0 24px" }}>
          {TABS.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)}
              style={{ padding:"10px 14px", background:"none", border:"none", borderBottom:`2px solid ${tab===t.id?"#E63329":"transparent"}`, color: tab===t.id?"#E63329":"#6B7280", fontSize:13, fontWeight: tab===t.id?600:400, cursor:"pointer", transition:"all .15s" }}>
              {t.label}
            </button>
          ))}
        </div>

        {/* Corps */}
        <div style={{ padding:"20px 24px", overflowY:"auto", flex:1 }}>
          {error && (
            <div style={{ background:"rgba(220,38,38,.08)", border:"1px solid rgba(220,38,38,.2)", borderRadius:8, padding:"9px 12px", marginBottom:16, fontSize:13, color:"#DC2626", display:"flex", alignItems:"center", gap:8 }}>
              <i className="bi bi-exclamation-triangle-fill"/>{error}
            </div>
          )}

          {/* ─ Onglet Général ─ */}
          {tab === "general" && (
            <div style={{ display:"flex", flexDirection:"column", gap:14 }}>
              <Field label="Entreprise *">
                <select className="inv-input" value={form.companyId} onChange={e => set("companyId", e.target.value)}>
                  <option value="">Sélectionner une entreprise…</option>
                  {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </Field>
              <Row>
                <Field label="Nom / Hostname *">
                  <input className="inv-input" value={form.name} onChange={e => set("name", e.target.value)} placeholder="PC-DUPONT-01"/>
                </Field>
                <Field label="Type *">
                  <select className="inv-input" value={form.type} onChange={e => set("type", e.target.value)}>
                    {TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                </Field>
              </Row>
              <Row>
                <Field label="Marque">
                  <input className="inv-input" value={form.brand} onChange={e => set("brand", e.target.value)} placeholder="Dell, HP, Lenovo…"/>
                </Field>
                <Field label="Modèle">
                  <input className="inv-input" value={form.model} onChange={e => set("model", e.target.value)} placeholder="OptiPlex 7090…"/>
                </Field>
              </Row>
              <Row>
                <Field label="Numéro de série">
                  <input className="inv-input" value={form.serialNumber} onChange={e => set("serialNumber", e.target.value)} placeholder="SN12345678"/>
                </Field>
                <Field label="Localisation">
                  <input className="inv-input" value={form.location} onChange={e => set("location", e.target.value)} placeholder="Bureau 3, Salle serveur…"/>
                </Field>
              </Row>
              <Field label="Statut">
                <select className="inv-input" value={form.status} onChange={e => set("status", e.target.value)}>
                  {STATUSES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                </select>
              </Field>
              <Field label="Notes internes">
                <textarea className="inv-input" rows={3} value={form.notes} onChange={e => set("notes", e.target.value)} placeholder="Notes visibles uniquement par l'admin…" style={{ resize:"vertical" }}/>
              </Field>
            </div>
          )}

          {/* ─ Onglet Technique ─ */}
          {tab === "tech" && (
            <div style={{ display:"flex", flexDirection:"column", gap:14 }}>
              <Field label="Processeur (CPU)">
                <input className="inv-input" value={form.cpu} onChange={e => set("cpu", e.target.value)} placeholder="Intel Core i7-12700, AMD Ryzen 5 5600X…"/>
              </Field>
              <Row>
                <Field label="RAM (Go)">
                  <input className="inv-input" type="number" value={form.ramGb} onChange={e => set("ramGb", e.target.value)} placeholder="16"/>
                </Field>
                <Field label="Disque (Go)">
                  <input className="inv-input" type="number" value={form.diskGb} onChange={e => set("diskGb", e.target.value)} placeholder="512"/>
                </Field>
                <Field label="Type disque">
                  <select className="inv-input" value={form.diskType} onChange={e => set("diskType", e.target.value)}>
                    <option value="SSD">SSD</option>
                    <option value="HDD">HDD</option>
                    <option value="NVMe">NVMe</option>
                  </select>
                </Field>
              </Row>
              <Row>
                <Field label="Système d'exploitation">
                  <input className="inv-input" value={form.os} onChange={e => set("os", e.target.value)} placeholder="Windows 11, Ubuntu…"/>
                </Field>
                <Field label="Version OS">
                  <input className="inv-input" value={form.osVersion} onChange={e => set("osVersion", e.target.value)} placeholder="22H2, 22.04 LTS…"/>
                </Field>
              </Row>
              <Row>
                <Field label="Adresse IP">
                  <input className="inv-input" value={form.ipAddress} onChange={e => set("ipAddress", e.target.value)} placeholder="192.168.1.50"/>
                </Field>
                <Field label="Adresse MAC">
                  <input className="inv-input" value={form.macAddress} onChange={e => set("macAddress", e.target.value)} placeholder="AA:BB:CC:DD:EE:FF"/>
                </Field>
              </Row>
            </div>
          )}

          {/* ─ Onglet Cycle de vie ─ */}
          {tab === "lifecycle" && (
            <div style={{ display:"flex", flexDirection:"column", gap:14 }}>
              <Row>
                <Field label="Date d'achat">
                  <input className="inv-input" type="date" value={form.purchaseDate} onChange={e => set("purchaseDate", e.target.value)}/>
                </Field>
                <Field label="Date de mise en marche">
                  <input className="inv-input" type="date" value={form.startDate} onChange={e => set("startDate", e.target.value)}/>
                </Field>
              </Row>
              <Field label="Fin de garantie">
                <input className="inv-input" type="date" value={form.warrantyEnd} onChange={e => set("warrantyEnd", e.target.value)}/>
              </Field>
              <Row>
                <Field label="Prix d'achat (€)">
                  <input className="inv-input" type="number" step="0.01" value={form.purchasePrice} onChange={e => set("purchasePrice", e.target.value)} placeholder="1200.00"/>
                </Field>
                <Field label="Fournisseur">
                  <input className="inv-input" value={form.supplier} onChange={e => set("supplier", e.target.value)} placeholder="Bureau en Gros, Dell Direct…"/>
                </Field>
              </Row>
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{ padding:"14px 24px", borderTop:"1px solid #E5E7EB", display:"flex", justifyContent:"flex-end", gap:10 }}>
          <button onClick={onClose} style={{ padding:"9px 18px", borderRadius:10, border:"1.5px solid #D1D5DB", background:"#fff", color:"#374151", fontSize:13, fontWeight:500, cursor:"pointer" }}>
            Annuler
          </button>
          <button onClick={handleSave} disabled={saving}
            style={{ padding:"9px 20px", borderRadius:10, border:"none", background:"#E63329", color:"#fff", fontSize:13, fontWeight:600, cursor: saving?"not-allowed":"pointer", display:"flex", alignItems:"center", gap:7, opacity: saving?.7:1 }}>
            {saving ? <><i className="bi bi-arrow-repeat spin"/> Enregistrement…</> : <><i className="bi bi-check2"/> {isEdit?"Enregistrer":"Ajouter"}</>}
          </button>
        </div>
      </div>
    </div>
  );
}

// Composants utilitaires du formulaire
function Field({ label, children }) {
  return (
    <div style={{ flex:1, minWidth:0 }}>
      <label style={{ display:"block", fontSize:11, fontWeight:700, color:"#6B7280", textTransform:"uppercase", letterSpacing:".05em", marginBottom:5 }}>{label}</label>
      {children}
    </div>
  );
}
function Row({ children }) {
  return <div style={{ display:"flex", gap:12 }}>{children}</div>;
}

// ─────────────────────────────────────────────
//  MODAL DÉTAIL — Fiche équipement
// ─────────────────────────────────────────────

function InventoryDetailModal({ item, onClose, onEdit, onDelete }) {
  const typeInfo    = getTypeInfo(item.type);
  const warrantyInfo= getWarrantyInfo(item.warrantyStatus);
  const statusInfo  = getStatusInfo(item.status);

  const InfoRow = ({ icon, label, value }) => value ? (
    <div style={{ display:"flex", alignItems:"flex-start", gap:10, padding:"9px 0", borderBottom:"1px solid #F3F4F6" }}>
      <i className={`bi ${icon}`} style={{ fontSize:14, color:"#9CA3AF", marginTop:1, flexShrink:0, width:16 }}/>
      <div style={{ flex:1 }}>
        <div style={{ fontSize:11, color:"#9CA3AF", fontWeight:600, textTransform:"uppercase", letterSpacing:".05em", marginBottom:1 }}>{label}</div>
        <div style={{ fontSize:13, color:"#1A1A1A", fontWeight:500 }}>{value}</div>
      </div>
    </div>
  ) : null;

  return (
    <div style={{ position:"fixed", inset:0, zIndex:1060, background:"rgba(0,0,0,.45)", backdropFilter:"blur(4px)", display:"flex", alignItems:"center", justifyContent:"center", padding:16 }}
      onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{ background:"#fff", borderRadius:16, width:"100%", maxWidth:540, maxHeight:"90vh", display:"flex", flexDirection:"column", boxShadow:"0 24px 80px rgba(0,0,0,.2)", overflow:"hidden" }}>

        {/* Banner coloré */}
        <div style={{ background:`linear-gradient(135deg, ${typeInfo.color}22, ${typeInfo.color}11)`, borderBottom:`1px solid ${typeInfo.color}33`, padding:"20px 24px" }}>
          <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between" }}>
            <div style={{ display:"flex", alignItems:"center", gap:14 }}>
              <div style={{ width:48, height:48, borderRadius:14, background:`${typeInfo.color}22`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:22, color:typeInfo.color }}>
                <i className={`bi ${typeInfo.icon}`}/>
              </div>
              <div>
                <div style={{ fontFamily:"Syne,sans-serif", fontSize:18, fontWeight:800, color:"#1A1A1A" }}>{item.name}</div>
                <div style={{ fontSize:12, color:"#6B7280", marginTop:2 }}>{item.companyName} · {typeInfo.label}</div>
              </div>
            </div>
            <button onClick={onClose} style={{ background:"rgba(0,0,0,.08)", border:"none", borderRadius:8, width:30, height:30, display:"flex", alignItems:"center", justifyContent:"center", cursor:"pointer", fontSize:13, color:"#374151" }}>
              <i className="bi bi-x-lg"/>
            </button>
          </div>
          <div style={{ display:"flex", gap:8, marginTop:14 }}>
            <Badge {...statusInfo}/>
            <Badge {...warrantyInfo}/>
          </div>
        </div>

        {/* Corps scrollable */}
        <div style={{ padding:"16px 24px", overflowY:"auto", flex:1 }}>

          {/* Identification */}
          <div style={{ fontSize:11, fontWeight:700, color:"#E63329", textTransform:"uppercase", letterSpacing:".08em", marginBottom:8 }}>Identification</div>
          <InfoRow icon="bi-tag"           label="Marque / Modèle"  value={[item.brand, item.model].filter(Boolean).join(" ")}/>
          <InfoRow icon="bi-upc"           label="N° de série"      value={item.serialNumber}/>
          <InfoRow icon="bi-geo-alt"       label="Localisation"     value={item.location}/>

          {/* Technique */}
          {(item.cpu || item.ramGb || item.diskGb || item.os || item.ipAddress) && (
            <>
              <div style={{ fontSize:11, fontWeight:700, color:"#E63329", textTransform:"uppercase", letterSpacing:".08em", marginTop:16, marginBottom:8 }}>Caractéristiques techniques</div>
              <InfoRow icon="bi-cpu"              label="Processeur"    value={item.cpu}/>
              <InfoRow icon="bi-memory"           label="RAM"           value={item.ramGb ? `${item.ramGb} Go` : null}/>
              <InfoRow icon="bi-device-hdd"       label="Disque"        value={item.diskGb ? `${item.diskGb} Go ${item.diskType || ""}`.trim() : null}/>
              <InfoRow icon="bi-window"           label="Système"       value={[item.os, item.osVersion].filter(Boolean).join(" ")}/>
              <InfoRow icon="bi-ethernet"         label="Adresse IP"    value={item.ipAddress}/>
              <InfoRow icon="bi-router"           label="Adresse MAC"   value={item.macAddress}/>
            </>
          )}

          {/* Cycle de vie */}
          {(item.purchaseDate || item.startDate || item.warrantyEnd || item.purchasePriceEur || item.supplier) && (
            <>
              <div style={{ fontSize:11, fontWeight:700, color:"#E63329", textTransform:"uppercase", letterSpacing:".08em", marginTop:16, marginBottom:8 }}>Cycle de vie</div>
              <InfoRow icon="bi-calendar-plus"    label="Date d'achat"           value={item.purchaseDate}/>
              <InfoRow icon="bi-calendar-check"   label="Mise en marche"         value={item.startDate}/>
              <InfoRow icon="bi-shield-check"     label="Fin de garantie"        value={item.warrantyEnd ? `${item.warrantyEnd} (${item.warrantyDaysLeft >= 0 ? `dans ${item.warrantyDaysLeft}j` : `expirée depuis ${Math.abs(item.warrantyDaysLeft)}j`})` : null}/>
              <InfoRow icon="bi-currency-euro"    label="Prix d'achat"           value={item.purchasePriceEur ? `${item.purchasePriceEur.toFixed(2)} €` : null}/>
              <InfoRow icon="bi-truck"            label="Fournisseur"            value={item.supplier}/>
            </>
          )}

          {/* Notes */}
          {item.notes && (
            <>
              <div style={{ fontSize:11, fontWeight:700, color:"#E63329", textTransform:"uppercase", letterSpacing:".08em", marginTop:16, marginBottom:8 }}>Notes internes</div>
              <div style={{ background:"#FFF8F8", border:"1px solid #FCA5A5", borderRadius:8, padding:"10px 12px", fontSize:13, color:"#374151", lineHeight:1.6 }}>
                {item.notes}
              </div>
            </>
          )}

          {/* Champs personnalisés Atera */}
          {item.customFields && Object.keys(item.customFields).length > 0 && (
            <>
              <div style={{ fontSize:11, fontWeight:700, color:"#3B82F6", textTransform:"uppercase", letterSpacing:".08em", marginTop:16, marginBottom:8 }}>
                Champs personnalisés Atera
              </div>
              {Object.entries(item.customFields).map(([key, val]) => (
                <div key={key} style={{ display:"flex", alignItems:"flex-start", gap:10, padding:"9px 0", borderBottom:"1px solid #F3F4F6" }}>
                  <i className="bi bi-tag-fill" style={{ fontSize:13, color:"#3B82F6", marginTop:1, flexShrink:0, width:16 }}/>
                  <div style={{ flex:1 }}>
                    <div style={{ fontSize:11, color:"#9CA3AF", fontWeight:600, textTransform:"uppercase", letterSpacing:".05em", marginBottom:1 }}>{key}</div>
                    <div style={{ fontSize:13, color:"#1A1A1A", fontWeight:500 }}>{val || "—"}</div>
                  </div>
                </div>
              ))}
            </>
          )}
        </div>

        {/* Footer */}
        <div style={{ padding:"12px 24px", borderTop:"1px solid #E5E7EB", display:"flex", gap:10 }}>
          <button disabled
            title="Suppression désactivée"
            style={{ padding:"8px 14px", borderRadius:10, border:"1.5px solid #E5E7EB", background:"#F3F4F6", color:"#9CA3AF", fontSize:13, fontWeight:500, cursor:"not-allowed", display:"flex", alignItems:"center", gap:6 }}>
            <i className="bi bi-trash3"/> Supprimer
          </button>
          <button disabled
            title="Modification désactivée"
            style={{ flex:1, padding:"8px 14px", borderRadius:10, border:"none", background:"#D1D5DB", color:"#9CA3AF", fontSize:13, fontWeight:600, cursor:"not-allowed", display:"flex", alignItems:"center", justifyContent:"center", gap:6 }}>
            <i className="bi bi-pencil"/> Modifier
          </button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
//  PAGE PRINCIPALE
// ─────────────────────────────────────────────

export default function InventoryPage() {
  const [items,       setItems]       = useState([]);
  const [companies,   setCompanies]   = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [error,       setError]       = useState("");
  const [viewMode,    setViewMode]    = useState("grid");  // grid | list
  const [page,        setPage]        = useState(0);
  const [showForm,    setShowForm]    = useState(false);
  const [editItem,    setEditItem]    = useState(null);
  const [detailItem,  setDetailItem]  = useState(null);

  // Filtres
  const [filterCompany,  setFilterCompany]  = useState("");
  const [filterType,     setFilterType]     = useState("");
  const [filterStatus,      setFilterStatus]      = useState("");
  const [filterAteraStatus, setFilterAteraStatus] = useState("");
  const [filterWarranty,    setFilterWarranty]    = useState("");
  const [search,         setSearch]         = useState("");

  // Charger les données
  // Charger les entreprises une seule fois au montage
  useEffect(() => {
    api.get("/companies/all")
      .then(res => {
        const data = res.data?.data || res.data || [];
        setCompanies(Array.isArray(data) ? data : []);
      })
      .catch(() => {});
  }, []);

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await getInventory({
        companyId: filterCompany  || undefined,
        type:      filterType     || undefined,
        status:    filterStatus   || undefined,
        search:    search         || undefined,
      });
      // Gérer les deux formats possibles : { data: [...] } ou directement [...]
      const data = res?.data || res || [];
      setItems(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [filterCompany, filterType, filterStatus, search]);

  // Filtre garantie côté client (calculé)
  const filtered = useMemo(() => {
    let result = items;
    if (filterWarranty) result = result.filter(i => i.warrantyStatus === filterWarranty);
    if (filterAteraStatus) result = result.filter(i => i.status === filterAteraStatus);
    return result;
  }, [items, filterWarranty, filterAteraStatus]);

  const paged = filtered.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);
  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);

  // Statistiques rapides
  const stats = useMemo(() => ({
    total:     items.length,
    active:    items.filter(i => i.status === "ACTIVE").length,
    expiring:  items.filter(i => i.warrantyStatus === "EXPIRING").length,
    expired:   items.filter(i => i.warrantyStatus === "EXPIRED").length,
  }), [items]);

  // Actions
  const handleSave = async (companyId, payload, id) => {
    const { updateInventoryItem } = await import("../services/inventoryService.js");
    if (id) {
      await updateInventoryItem(id, payload);
      await load();
    }
  };

  // La suppression d'un asset n'est pas disponible depuis l'inventaire
  // Les assets sont gérés via le parc informatique et la sync Atera
  const handleDelete = async (id) => {
    alert("La suppression d'un équipement se fait depuis le module Parc informatique.");
  };

  const handleExport = async () => {
    await exportInventoryXlsx({ companyId:filterCompany, type:filterType, inventoryStatus:filterStatus, search });
  };

  const resetFilters = () => {
    setFilterCompany(""); setFilterType(""); setFilterStatus("");
    setFilterWarranty(""); setFilterAteraStatus(""); setSearch(""); setPage(0);
  };

  const hasFilters = filterCompany || filterType || filterStatus || filterAteraStatus || filterWarranty || search;

  return (
    <>
      <div className="topbar">
        <div className="topbar-title">Inventaire <span>informatique</span></div>
      </div>

      <div className="page-body">

        {/* ── Stats rapides ── */}
        <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:12, marginBottom:20 }}>
          {[
            { label:"Total équipements", value:stats.total,    icon:"bi-clipboard2-check",  color:"#3B82F6", bg:"rgba(59,130,246,.08)"  },
            { label:"En service",         value:stats.active,   icon:"bi-check-circle",       color:"#10B981", bg:"rgba(16,185,129,.08)"  },
            { label:"Garantie expirante", value:stats.expiring, icon:"bi-exclamation-circle", color:"#F59E0B", bg:"rgba(245,158,11,.08)"  },
            { label:"Garantie expirée",   value:stats.expired,  icon:"bi-x-circle",           color:"#DC2626", bg:"rgba(220,38,38,.08)"   },
          ].map((s,i) => (
            <div key={i} style={{ background:"#fff", border:"1px solid #E5E7EB", borderRadius:12, padding:"14px 16px", display:"flex", alignItems:"center", gap:12 }}>
              <div style={{ width:40, height:40, borderRadius:10, background:s.bg, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
                <i className={`bi ${s.icon}`} style={{ fontSize:18, color:s.color }}/>
              </div>
              <div>
                <div style={{ fontFamily:"Syne,sans-serif", fontSize:22, fontWeight:800, color:"#1A1A1A", lineHeight:1 }}>{s.value}</div>
                <div style={{ fontSize:12, color:"#6B7280", marginTop:2 }}>{s.label}</div>
              </div>
            </div>
          ))}
        </div>

        {/* ── En-tête ── */}
        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:14, flexWrap:"wrap", gap:10 }}>
          <div style={{ fontSize:13, color:"#6B7280" }}>
            {filtered.length} équipement{filtered.length > 1 ? "s" : ""}
            {hasFilters && <span style={{ color:"#E63329", marginLeft:6 }}>· Filtrés</span>}
          </div>
          <div style={{ display:"flex", gap:8 }}>
            {/* Toggle vue */}
            <div style={{ display:"flex", background:"#F3F4F6", borderRadius:8, padding:3, gap:2 }}>
              {[["grid","bi-grid-3x3-gap"],["list","bi-list-ul"]].map(([m,ico]) => (
                <button key={m} onClick={() => setViewMode(m)}
                  style={{ padding:"5px 10px", borderRadius:6, border:"none", cursor:"pointer", fontSize:14, transition:"all .15s", background: viewMode===m?"#fff":"transparent", color: viewMode===m?"#1A1A1A":"#9CA3AF", boxShadow: viewMode===m?"0 1px 3px rgba(0,0,0,.1)":"none" }}>
                  <i className={`bi ${ico}`}/>
                </button>
              ))}
            </div>
            <button onClick={handleExport}
              style={{ display:"flex", alignItems:"center", gap:6, padding:"7px 14px", borderRadius:8, border:"1.5px solid #D1D5DB", background:"#fff", color:"#374151", fontSize:13, fontWeight:500, cursor:"pointer" }}>
              <i className="bi bi-file-earmark-excel"/> Export Excel
            </button>

          </div>
        </div>

        {/* ── Filtres ── */}
        <div style={{ display:"flex", gap:8, marginBottom:16, flexWrap:"wrap", alignItems:"center" }}>
          <div style={{ position:"relative", flex:"1 1 200px", maxWidth:260 }}>
            <i className="bi bi-search" style={{ position:"absolute", left:10, top:"50%", transform:"translateY(-50%)", color:"#9CA3AF", fontSize:13 }}/>
            <input value={search} onChange={e => { setSearch(e.target.value); setPage(0); }}
              placeholder="Rechercher…"
              style={{ width:"100%", padding:"8px 10px 8px 32px", border:"1.5px solid #D1D5DB", borderRadius:8, fontSize:13, color:"#1A1A1A", outline:"none", background:"#fff", boxSizing:"border-box" }}/>
          </div>

          <select value={filterCompany} onChange={e => { setFilterCompany(e.target.value); setPage(0); }}
            style={{ padding:"8px 10px", border:"1.5px solid #D1D5DB", borderRadius:8, fontSize:13, color:"#374151", background:"#fff", cursor:"pointer" }}>
            <option value="">Toutes les entreprises</option>
            {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>

          <select value={filterType} onChange={e => { setFilterType(e.target.value); setPage(0); }}
            style={{ padding:"8px 10px", border:"1.5px solid #D1D5DB", borderRadius:8, fontSize:13, color:"#374151", background:"#fff", cursor:"pointer" }}>
            <option value="">Tous les types</option>
            {TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>

          <select value={filterStatus} onChange={e => { setFilterStatus(e.target.value); setPage(0); }}
            style={{ padding:"8px 10px", border:"1.5px solid #D1D5DB", borderRadius:8, fontSize:13, color:"#374151", background:"#fff", cursor:"pointer" }}>
            <option value="">Tous les statuts</option>
            {STATUSES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>

          {/* Filtre disponibilité Atera — En ligne / Hors ligne */}
          <select value={filterAteraStatus} onChange={e => { setFilterAteraStatus(e.target.value); setPage(0); }}
            style={{ padding:"8px 10px", border:"1.5px solid #D1D5DB", borderRadius:8, fontSize:13, color:"#374151", background:"#fff", cursor:"pointer" }}>
            <option value="">Toutes les disponibilités</option>
            {ATERA_STATUSES.map(s => (
              <option key={s.value} value={s.value}>{s.label}</option>
            ))}
          </select>

          <select value={filterWarranty} onChange={e => { setFilterWarranty(e.target.value); setPage(0); }}
            style={{ padding:"8px 10px", border:"1.5px solid #D1D5DB", borderRadius:8, fontSize:13, color:"#374151", background:"#fff", cursor:"pointer" }}>
            <option value="">Toutes les garanties</option>
            {WARRANTY.map(w => <option key={w.value} value={w.value}>{w.label}</option>)}
          </select>

          {hasFilters && (
            <button onClick={resetFilters}
              style={{ display:"flex", alignItems:"center", gap:5, padding:"7px 12px", borderRadius:8, border:"1.5px solid #FCA5A5", background:"rgba(220,38,38,.06)", color:"#DC2626", fontSize:13, cursor:"pointer" }}>
              <i className="bi bi-x-circle"/> Réinitialiser
            </button>
          )}
        </div>

        {/* ── Alerte garanties expirantes ── */}
        {stats.expiring > 0 && (
          <div style={{ display:"flex", alignItems:"center", gap:10, padding:"10px 14px", background:"rgba(245,158,11,.08)", border:"1px solid rgba(245,158,11,.3)", borderRadius:10, marginBottom:14, fontSize:13 }}>
            <i className="bi bi-exclamation-triangle-fill" style={{ color:"#F59E0B", fontSize:16 }}/>
            <span style={{ color:"#92400E", fontWeight:500 }}>
              {stats.expiring} équipement{stats.expiring>1?"s":""} avec une garantie expirant dans moins de 30 jours.
            </span>
            <button onClick={() => setFilterWarranty("EXPIRING")}
              style={{ marginLeft:"auto", background:"none", border:"1px solid #F59E0B", color:"#F59E0B", borderRadius:6, padding:"3px 10px", fontSize:12, cursor:"pointer", fontWeight:600 }}>
              Voir
            </button>
          </div>
        )}

        {/* ── Contenu ── */}
        {loading ? (
          <div style={{ textAlign:"center", padding:60, color:"#9CA3AF" }}>
            <i className="bi bi-arrow-repeat spin" style={{ fontSize:28, display:"block", marginBottom:10 }}/>
            Chargement de l'inventaire…
          </div>
        ) : error ? (
          <div style={{ textAlign:"center", padding:60, color:"#DC2626" }}>
            <i className="bi bi-exclamation-circle" style={{ fontSize:28, display:"block", marginBottom:10 }}/>
            {error}
          </div>
        ) : filtered.length === 0 ? (
          <div style={{ textAlign:"center", padding:60, color:"#9CA3AF" }}>
            <i className="bi bi-clipboard2-x" style={{ fontSize:40, display:"block", marginBottom:12, opacity:.4 }}/>
            <div style={{ fontSize:15, fontWeight:600, color:"#374151", marginBottom:6 }}>Aucun équipement trouvé</div>
            <div style={{ fontSize:13 }}>Ajoutez votre premier équipement ou modifiez les filtres.</div>
          </div>
        ) : viewMode === "grid" ? (

          // ── Vue grille ──
          <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(260px,1fr))", gap:12 }}>
            {paged.map(item => {
              const typeInfo    = getTypeInfo(item.type);
              const warrantyInfo= getWarrantyInfo(item.warrantyStatus);
              const statusInfo  = getStatusInfo(item.status);
              return (
                <div key={item.id} onClick={() => setDetailItem(item)}
                  style={{ background:"#fff", border:`1.5px solid #E5E7EB`, borderRadius:12, padding:16, cursor:"pointer", transition:"transform .18s, border-color .18s, box-shadow .18s" }}
                  onMouseEnter={e => { e.currentTarget.style.transform="translateY(-2px)"; e.currentTarget.style.boxShadow="0 4px 16px rgba(0,0,0,.08)"; e.currentTarget.style.borderColor="#D1D5DB"; }}
                  onMouseLeave={e => { e.currentTarget.style.transform=""; e.currentTarget.style.boxShadow=""; e.currentTarget.style.borderColor="#E5E7EB"; }}
                >
                  <div style={{ display:"flex", alignItems:"flex-start", justifyContent:"space-between", marginBottom:12 }}>
                    <div style={{ width:42, height:42, borderRadius:12, background:`${typeInfo.color}18`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:20, color:typeInfo.color }}>
                      <i className={`bi ${typeInfo.icon}`}/>
                    </div>
                    <div style={{ display:"flex", flexDirection:"column", alignItems:"flex-end", gap:4 }}>
                      <Badge {...statusInfo}/>
                      {item.warrantyStatus !== "NONE" && <Badge {...warrantyInfo}/>}
                    </div>
                  </div>

                  {/* Hostname de l'équipement */}
                  <div style={{ fontFamily:"monospace", fontSize:13, fontWeight:700, color:"#1A1A1A", marginBottom:2, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{item.hostname}</div>
                  <div style={{ display:"flex", alignItems:"center", gap:6, marginBottom:8 }}>
                    <div style={{ fontSize:12, color:"#E63329", fontWeight:600 }}>{item.companyName}</div>
                    {item.status && (
                      <span style={{ display:"inline-flex", alignItems:"center", gap:3, fontSize:11, fontWeight:600,
                        color: item.status === "ONLINE" ? "#10B981" : "#DC2626" }}>
                        <span style={{ width:6, height:6, borderRadius:"50%", background: item.status === "ONLINE" ? "#10B981" : "#DC2626", display:"inline-block" }}/>
                        {item.status === "ONLINE" ? "En ligne" : "Hors ligne"}
                      </span>
                    )}
                  </div>

                  {/* Specs rapides */}
                  <div style={{ display:"flex", flexDirection:"column", gap:3 }}>
                    {item.manufacturer && <div style={{ fontSize:12, color:"#6B7280" }}><i className="bi bi-tag me-1"/>{item.manufacturer} {item.model}</div>}
                    {item.cpu   && <div style={{ fontSize:12, color:"#6B7280" }}><i className="bi bi-cpu me-1"/>{item.cpu}</div>}
                    {item.ramGb && <div style={{ fontSize:12, color:"#6B7280" }}><i className="bi bi-memory me-1"/>{item.ramGb} Go RAM{item.diskTotalGb ? ` · ${item.diskTotalGb} Go` : ""}</div>}
                    {item.os    && <div style={{ fontSize:12, color:"#6B7280" }}><i className="bi bi-window me-1"/>{item.os}</div>}
                    {/* Champs personnalisés — affiche les 2 premiers */}
                    {item.customFields && Object.entries(item.customFields).slice(0,2).map(([k,v]) => (
                      <div key={k} style={{ fontSize:12, color:"#3B82F6" }}><i className="bi bi-tag-fill me-1"/>{k}: {v}</div>
                    ))}
                    {item.warrantyEnd && (
                      <div style={{ fontSize:12, color: item.warrantyStatus==="EXPIRED"?"#DC2626":item.warrantyStatus==="EXPIRING"?"#F59E0B":"#6B7280", marginTop:4 }}>
                        <i className="bi bi-shield-check me-1"/>
                        {item.warrantyStatus==="EXPIRED" ? `Garantie expirée (${item.warrantyEnd})` :
                         item.warrantyStatus==="EXPIRING"? `Expire le ${item.warrantyEnd} (${item.warrantyDaysLeft}j)` :
                         `Garantie jusqu'au ${item.warrantyEnd}`}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

        ) : (

          // ── Vue liste (tableau) ──
          <div style={{ background:"#fff", border:"1px solid #E5E7EB", borderRadius:12, overflow:"hidden" }}>
            <table style={{ width:"100%", borderCollapse:"collapse" }}>
              <thead>
                <tr style={{ background:"#F9FAFB", borderBottom:"1px solid #E5E7EB" }}>
                  {["Équipement","Entreprise","Type","CPU","RAM","Disque","OS","Mise en marche","Garantie","Statut",""].map(h => (
                    <th key={h} style={{ padding:"10px 14px", textAlign:"left", fontSize:11, fontWeight:700, color:"#6B7280", textTransform:"uppercase", letterSpacing:".05em", whiteSpace:"nowrap" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {paged.map((item, idx) => {
                  const typeInfo    = getTypeInfo(item.type);
                  const warrantyInfo= getWarrantyInfo(item.warrantyStatus);
                  const statusInfo  = getStatusInfo(item.status);
                  return (
                    <tr key={item.id} onClick={() => setDetailItem(item)}
                      style={{ borderBottom:"1px solid #F3F4F6", cursor:"pointer", background: idx%2===0?"#fff":"#FAFAFA", transition:"background .12s" }}
                      onMouseEnter={e => e.currentTarget.style.background="#FFF8F8"}
                      onMouseLeave={e => e.currentTarget.style.background=idx%2===0?"#fff":"#FAFAFA"}
                    >
                      <td style={{ padding:"10px 14px" }}>
                        <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                          <div style={{ width:32, height:32, borderRadius:8, background:`${typeInfo.color}18`, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0, color:typeInfo.color, fontSize:15 }}>
                            <i className={`bi ${typeInfo.icon}`}/>
                          </div>
                          <div>
                            <div style={{ fontFamily:"monospace", fontWeight:700, fontSize:13, color:"#1A1A1A" }}>{item.hostname}</div>
                            {item.serialNumber && <div style={{ fontSize:11, color:"#9CA3AF" }}>SN: {item.serialNumber}</div>}
                          </div>
                        </div>
                      </td>
                      <td style={{ padding:"10px 14px", fontSize:13, color:"#E63329", fontWeight:600 }}>{item.companyName}</td>
                      <td style={{ padding:"10px 14px", fontSize:12, color:"#6B7280" }}>{typeInfo.label}</td>
                      <td style={{ padding:"10px 14px", fontSize:12, color:"#374151", maxWidth:150, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{item.cpu || "—"}</td>
                      <td style={{ padding:"10px 14px", fontSize:12, color:"#374151" }}>{item.ramGb ? `${item.ramGb} Go` : "—"}</td>
                      <td style={{ padding:"10px 14px", fontSize:12, color:"#374151" }}>{item.diskGb ? `${item.diskGb} Go` : "—"}</td>
                      <td style={{ padding:"10px 14px", fontSize:12, color:"#374151" }}>{item.os || "—"}</td>
                      <td style={{ padding:"10px 14px", fontSize:12, color:"#6B7280" }}>{item.startDate || "—"}</td>
                      <td style={{ padding:"10px 14px" }}>{item.warrantyStatus !== "NONE" ? <Badge {...warrantyInfo}/> : <span style={{ fontSize:12, color:"#9CA3AF" }}>—</span>}</td>
                      <td style={{ padding:"10px 14px" }}><Badge {...statusInfo}/></td>
                      <td style={{ padding:"10px 14px" }}>
                        <div style={{ display:"flex", gap:6 }} onClick={e => e.stopPropagation()}>
                          <button onClick={() => { setEditItem(item); setShowForm(true); }}
                            style={{ width:28, height:28, borderRadius:7, border:"1px solid #E5E7EB", background:"#fff", cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", fontSize:13, color:"#6B7280" }}>
                            <i className="bi bi-pencil"/>
                          </button>
                          <button onClick={() => handleDelete(item.id)}
                            style={{ width:28, height:28, borderRadius:7, border:"1px solid #FCA5A5", background:"rgba(220,38,38,.06)", cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", fontSize:13, color:"#DC2626" }}>
                            <i className="bi bi-trash3"/>
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* ── Pagination ── */}
        {totalPages > 1 && (
          <div style={{ display:"flex", alignItems:"center", justifyContent:"center", gap:6, marginTop:20 }}>
            <button onClick={() => setPage(p => Math.max(0, p-1))} disabled={page===0}
              style={{ padding:"6px 12px", borderRadius:8, border:"1.5px solid #D1D5DB", background:"#fff", color: page===0?"#D1D5DB":"#374151", cursor: page===0?"not-allowed":"pointer", fontSize:13 }}>
              <i className="bi bi-chevron-left"/>
            </button>
            {Array.from({length:totalPages}, (_,i) => (
              <button key={i} onClick={() => setPage(i)}
                style={{ width:32, height:32, borderRadius:8, border:`1.5px solid ${page===i?"#E63329":"#D1D5DB"}`, background: page===i?"#E63329":"#fff", color: page===i?"#fff":"#374151", cursor:"pointer", fontSize:13, fontWeight: page===i?700:400 }}>
                {i+1}
              </button>
            ))}
            <button onClick={() => setPage(p => Math.min(totalPages-1, p+1))} disabled={page===totalPages-1}
              style={{ padding:"6px 12px", borderRadius:8, border:"1.5px solid #D1D5DB", background:"#fff", color: page===totalPages-1?"#D1D5DB":"#374151", cursor: page===totalPages-1?"not-allowed":"pointer", fontSize:13 }}>
              <i className="bi bi-chevron-right"/>
            </button>
          </div>
        )}
      </div>

      {/* ── Modals ── */}
      {showForm && (
        <InventoryFormModal
          item={editItem}
          companies={companies}
          onSave={handleSave}
          onClose={() => { setShowForm(false); setEditItem(null); }}
        />
      )}
      {detailItem && (
        <InventoryDetailModal
          item={detailItem}
          onClose={() => setDetailItem(null)}
          onEdit={item => { setEditItem(item); setShowForm(true); }}
          onDelete={handleDelete}
        />
      )}

      <style>{`
        .inv-input{display:block;width:100%;padding:9px 12px;border:1.5px solid #D1D5DB;border-radius:8px;font-size:13px;color:#111827;background:#F9FAFB;outline:none;transition:border-color .15s;box-sizing:border-box;font-family:inherit}
        .inv-input:focus{border-color:#E63329;background:#fff}
        .inv-input::placeholder{color:#9CA3AF}
        @keyframes spin{to{transform:rotate(360deg)}}
        .spin{animation:spin .7s linear infinite;display:inline-block}
      `}</style>
    </>
  );
}